import commonStyles from './common.module.scss';
import styles from './general-configs.module.scss';
import { useScenarioForm } from './contexts';
import {
  TanstackFieldWrapper,
  NumberField,
  DropdownField,
} from 'components/form-fields-2';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { useEffect, useMemo, useState } from 'react';
import { capitalize } from 'lodash-es';
import { isGeneralConfigModified } from './utils';
import { Button, InlineNotification } from '@carbon/react';
import { Reset, Save } from '@carbon/react/icons';
import { ConfigSkeleton } from './config-skeleton';
import { createResourceAtom } from 'lib/atoms';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { scenarioFormDefaultValues } from './default-values';
import { SaveConfigModal } from './save-config-modal';
import { useSaveConfigModal } from './hooks';
import { saveGeneralConfigAtom } from './atoms';
import { ProductionDataSourceHierarchy } from './production-data-source-hierarchy';
import type { GeneralConfig as GeneralConfigResponse } from 'types/dca-scenarios-api/responses';

const FALLBACK_ATOM = atom(null);

export const GeneralConfigs = () => {
  const [expanded, setExpanded] = useState(true);
  const [refetchOptions, setRefetchOptions] = useState<number>(0);

  const {
    form,
    defaultValues,
    selectedConfig,
    setFieldBaseline,
    updateFieldDefault,
  } = useScenarioForm();
  const { configModalState, onClose, onOpen, error, setError } =
    useSaveConfigModal();

  const saveGeneralConfigMutation = useSetAtom(saveGeneralConfigAtom);

  const generalConfigHref = selectedConfig?.generalConfig?.href;
  const generalConfigResourceHref =
    selectedConfig?.resources.generalConfigs?.href;

  const resourceAtom = useMemo(() => {
    if (!generalConfigHref) return FALLBACK_ATOM;

    return createResourceAtom<any>(generalConfigHref);
  }, [generalConfigHref]);

  const configOptionsAtom = useMemo(() => {
    if (!generalConfigResourceHref) return FALLBACK_ATOM;

    return createResourceAtom<any>(generalConfigResourceHref);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalConfigResourceHref, refetchOptions]);

  const configOptions = useAtomValue(configOptionsAtom);

  const resource = useAtomValue(resourceAtom);

  useEffect(() => {
    if (resource?.data) {
      const generalConfigData = {
        id: resource.data.id,
        name: resource.data.name,
        historyCutoffMonths: resource.data.historyCutoffMonths,
        earlyTimeCutoffMonths: resource.data.earlyTimeCutoffMonths,
        detectPeakRate: resource.data.detectPeakRate,
        peakRateSearchWindow:
          resource.data.peakRateSearchWindow ??
          scenarioFormDefaultValues.generalConfig.peakRateSearchWindow,
        productionDataSourceHierarchy:
          (resource.data as Partial<GeneralConfigResponse>)
            .productionDataSourceHierarchy ??
          scenarioFormDefaultValues.generalConfig.productionDataSourceHierarchy,
      };

      setFieldBaseline('generalConfig', generalConfigData);
    } else if (resource === null && selectedConfig?.id) {
      const fallback = scenarioFormDefaultValues.generalConfig;
      setFieldBaseline('generalConfig', fallback);
    }
  }, [resource, setFieldBaseline, selectedConfig?.id, form]);

  const formattedOptions = useMemo(() => {
    if (!(configOptions && configOptions.data?.results)) return [];

    return configOptions.data.results;
  }, [configOptions]);

  const handleSaveConfig = (name: string, isNew: boolean) => {
    const configId = form.getFieldValue('generalConfig.id');

    let url: string;
    let method: 'POST' | 'PATCH';

    if (isNew) {
      url = `/dca_scenarios/general_configs`;
      method = 'POST';
    } else {
      url = `/dca_scenarios/general_configs/${configId}`;
      method = 'PATCH';
    }

    const detectPeakRate = form.getFieldValue(
      'generalConfig.detectPeakRate'
    ) as boolean;

    const payload = {
      name,
      detectPeakRate: form.getFieldValue(
        'generalConfig.detectPeakRate'
      ) as boolean,
      peakRateSearchWindow: detectPeakRate
        ? (form.getFieldValue('generalConfig.peakRateSearchWindow') as number)
        : null,
      earlyTimeCutoffMonths: form.getFieldValue(
        'generalConfig.earlyTimeCutoffMonths'
      ) as number,
      historyCutoffMonths: form.getFieldValue(
        'generalConfig.historyCutoffMonths'
      ) as number,
      productionDataSourceHierarchy:
        form.getFieldValue('generalConfig.productionDataSourceHierarchy') ??
        scenarioFormDefaultValues.generalConfig.productionDataSourceHierarchy,
    };

    saveGeneralConfigMutation({
      url,
      type: method,
      data: payload,
      onSuccess: (data) => {
        onClose();
        form.setFieldValue('generalConfig', { id: data.id, ...payload });
        updateFieldDefault('generalConfig', { id: data.id, ...payload });
        setRefetchOptions(refetchOptions + 1);
      },
      onError: (error) => {
        if (error?.errors) {
          const errors = error.errors;

          let msg = '';
          errors.forEach((error) => {
            msg += `${error.message}`;
          });

          setError(msg);
        }
      },
    });
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <AccordionSummary>
          <h2 className={commonStyles.configFormTitle}>General</h2>
        </AccordionSummary>
        <AccordionDetails>
          <div className={commonStyles.configFormContainer}>
            <form.Subscribe
              selector={(state) => {
                const generalConfig = state.values.generalConfig;
                const isModified =
                  !!generalConfig &&
                  !!defaultValues?.generalConfig &&
                  isGeneralConfigModified(
                    generalConfig,
                    defaultValues.generalConfig
                  );

                const fieldMeta = state.fieldMeta;
                const hasErrors = (meta: { errors?: unknown[] } | undefined) =>
                  meta?.errors !== null &&
                  meta?.errors?.length &&
                  meta?.errors?.length > 0;

                const isValid =
                  !hasErrors(fieldMeta['generalConfig.historyCutoffMonths']) &&
                  !hasErrors(
                    fieldMeta['generalConfig.earlyTimeCutoffMonths']
                  ) &&
                  !hasErrors(fieldMeta['generalConfig.detectPeakRate']) &&
                  !hasErrors(fieldMeta['generalConfig.peakRateSearchWindow']);

                return { generalConfig, isModified, isValid };
              }}
            >
              {({ generalConfig, isModified, isValid }) => {
                return (
                  <>
                    {!generalConfig && <ConfigSkeleton id="General" />}
                    {generalConfig && (
                      <>
                        <div className={styles.inputsContainer}>
                          <form.Field name="generalConfig.id">
                            {(field) => (
                              <TanstackFieldWrapper<string | undefined>
                                field={field}
                              >
                                {(props) => (
                                  <DropdownField
                                    id="generalConfig.id"
                                    label="Select a config"
                                    titleText="Config"
                                    items={formattedOptions}
                                    itemToString={(item: any) =>
                                      capitalize(item.name || '')
                                    }
                                    {...props}
                                    onChange={(value) => {
                                      const generalConfigData = {
                                        id: value.id,
                                        name: value.name,
                                        historyCutoffMonths:
                                          value.historyCutoffMonths,
                                        earlyTimeCutoffMonths:
                                          value.earlyTimeCutoffMonths,
                                        detectPeakRate: value.detectPeakRate,
                                        peakRateSearchWindow:
                                          value.peakRateSearchWindow ??
                                          scenarioFormDefaultValues
                                            .generalConfig.peakRateSearchWindow,
                                        productionDataSourceHierarchy:
                                          (
                                            value as Partial<GeneralConfigResponse>
                                          ).productionDataSourceHierarchy ??
                                          scenarioFormDefaultValues
                                            .generalConfig
                                            .productionDataSourceHierarchy,
                                      };

                                      form.setFieldValue(
                                        'generalConfig',
                                        generalConfigData
                                      );
                                      updateFieldDefault(
                                        'generalConfig',
                                        generalConfigData
                                      );
                                    }}
                                    value={
                                      generalConfig.id
                                        ? {
                                            id: generalConfig.id,
                                            name:
                                              formattedOptions.find(
                                                (item: any) =>
                                                  item.id === generalConfig.id
                                              )?.name ?? '',
                                          }
                                        : null
                                    }
                                  />
                                )}
                              </TanstackFieldWrapper>
                            )}
                          </form.Field>

                          <form.Field name="generalConfig.historyCutoffMonths">
                            {(field) => (
                              <TanstackFieldWrapper<number | null>
                                field={field}
                              >
                                {(props) => (
                                  <NumberField
                                    id="historyCutoffMonths"
                                    label="History Cutoff Months"
                                    {...props}
                                  />
                                )}
                              </TanstackFieldWrapper>
                            )}
                          </form.Field>

                          <form.Field name="generalConfig.earlyTimeCutoffMonths">
                            {(field) => (
                              <TanstackFieldWrapper<number | null>
                                field={field}
                              >
                                {(props) => (
                                  <NumberField
                                    id="earlyTimeCutoffMonths"
                                    label="Early Time Cutoff Months"
                                    {...props}
                                  />
                                )}
                              </TanstackFieldWrapper>
                            )}
                          </form.Field>
                        </div>

                        <form.Field name="generalConfig.productionDataSourceHierarchy">
                          {(field) => (
                            <ProductionDataSourceHierarchy
                              sources={field.state.value ?? []}
                              onChange={field.handleChange}
                            />
                          )}
                        </form.Field>

                        {generalConfig?.id && isModified && (
                          <InlineNotification
                            className={commonStyles.unsavedChangesNotification}
                            lowContrast
                            hideCloseButton
                            kind="info"
                            title="Unsaved changes"
                            subtitle="Make sure to save"
                          />
                        )}

                        <div className={commonStyles.buttonContainer}>
                          <Button
                            size="md"
                            kind="tertiary"
                            onClick={() => {
                              const baseline =
                                defaultValues?.generalConfig ?? null;
                              if (baseline === null) return;
                              form.setFieldValue('generalConfig', baseline);
                            }}
                            renderIcon={Reset}
                            disabled={!isModified}
                          >
                            Reset
                          </Button>

                          {(!generalConfig?.id || isModified) && (
                            <Button
                              size="md"
                              kind="primary"
                              disabled={!isValid}
                              onClick={() => {
                                const isNewConfig = !generalConfig.id;

                                const configName =
                                  formattedOptions.find(
                                    (item: any) => item.id === generalConfig?.id
                                  )?.name ?? '';

                                onOpen(isNewConfig, configName);
                              }}
                              renderIcon={Save}
                            >
                              Save
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </>
                );
              }}
            </form.Subscribe>
          </div>
        </AccordionDetails>
      </Accordion>
      {configModalState.isOpen && (
        <SaveConfigModal
          section="general"
          config={configModalState.config}
          defaultOption={configModalState.defaultOption}
          disableOptions={configModalState.disableOptions}
          onClose={() => {
            onClose();
            setError(null);
          }}
          error={error}
          onSave={handleSaveConfig}
        />
      )}
    </>
  );
};
